import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Send, Sparkles, FileText, Paperclip, X, Loader2, CornerDownLeft, ArrowLeft, CalendarClock,
} from 'lucide-react';
import { meetingSchedulerAPI } from '../../lib/api';
import { QUICK_TEMPLATES, initials } from './outboxUtils';

const firstName = (name) => {
  const f = (name || 'there').trim().split(/\s+/)[0];
  return f.charAt(0).toUpperCase() + f.slice(1).toLowerCase();
};

export const ReplyDrawer = ({
  reply, conversation, convoLoading,
  replyText, setReplyText, replySending, replyAttachments,
  replyAiLoading, onAiDraft, onOpenAttachPicker, onToggleAttachment,
  loadingDocs, libraryDocs, onSend, onClose, onCancel, onViewLead,
}) => {
  const insertTemplate = (tpl) => {
    const filled = tpl.body.replace(/\{name\}/g, firstName(reply?.lead_name));
    setReplyText((prev) => (prev.trim() ? `${prev}\n\n${filled}` : filled));
  };

  const [bookingLoading, setBookingLoading] = useState(false);
  const bookingCache = useRef({});
  const insertBookingLink = async () => {
    const leadId = reply?.lead_id;
    if (!leadId) { toast.error('No lead linked to this conversation'); return; }
    let link = bookingCache.current[leadId];
    if (!link) {
      setBookingLoading(true);
      try {
        const res = await meetingSchedulerAPI.getLeadBookingLink(leadId);
        link = res?.data?.booking_link;
        if (link) bookingCache.current[leadId] = link;
      } catch (e) {
        toast.error('Could not get booking link', { description: 'Connect Google Calendar in Integrations first.' });
        setBookingLoading(false);
        return;
      }
      setBookingLoading(false);
    }
    if (!link) { toast.error('Connect Google Calendar in Integrations to generate a booking link'); return; }
    if (replyText.includes(link)) { toast.info('Booking link is already in your reply'); return; }
    const snippet = `Hi ${firstName(reply?.lead_name)}, feel free to grab a time that works best for you here: ${link}`;
    setReplyText((prev) => (prev.trim() ? `${prev}\n\n${snippet}` : snippet));
    toast.success('Booking link added to your reply');
  };
  return (
    <div className="flex flex-col h-full min-h-0" data-testid="reply-conversation-drawer">
      <div className="px-5 py-3 border-b border-border shrink-0 flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-7 w-7 lg:hidden" onClick={onClose}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="h-9 w-9 rounded-full bg-emerald-500/10 text-emerald-600 flex items-center justify-center text-xs font-semibold shrink-0">{initials(reply.lead_name)}</div>
        <div className="min-w-0 flex-1">
          <h2 className="text-[15px] font-semibold truncate">{reply.subject}</h2>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{reply.lead_name}</span>
            {reply.lead_company && <span>· {reply.lead_company}</span>}
          </div>
        </div>
        <Button variant="outline" size="sm" className="h-7 px-2 text-xs shrink-0" onClick={() => onViewLead(reply.lead_id)} data-testid="reply-view-lead-btn">View lead</Button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-4 min-h-0" data-testid="reply-conversation-messages">
        {convoLoading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-emerald-500" /></div>
        ) : (conversation.length > 0 ? conversation : [{ id: reply.id, direction: 'inbound', body: reply.snippet, sent_at: reply.received_at }]).map((m) => {
          const inb = (m.direction || 'outbound') === 'inbound';
          return (
            <div key={m.id} className={`max-w-[80%] ${inb ? 'self-start' : 'self-end'}`} data-testid={`convo-msg-${m.id}`}>
              <div className={`p-3 rounded-2xl border shadow-sm ${inb ? 'bg-muted/40 border-border rounded-tl-sm' : 'bg-blue-500/10 border-blue-500/20 rounded-tr-sm'}`}>
                <p className="text-sm whitespace-pre-wrap text-foreground/90">{m.body}</p>
              </div>
              <div className={`flex items-center gap-1 mt-1 text-[10px] text-muted-foreground ${inb ? 'justify-start' : 'justify-end'}`}>
                {inb ? <CornerDownLeft className="h-3 w-3 text-emerald-600" /> : <Send className="h-3 w-3 text-blue-600" />}
                <span>{inb ? reply.lead_name : 'You'}</span><span>·</span>
                <span>{m.sent_at ? new Date(m.sent_at).toLocaleString() : ''}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="border-t border-border p-4 bg-muted/20 shrink-0">
        {/* Composer toolbar: Draft with AI · Insert template · Attach file */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={onAiDraft} disabled={replyAiLoading} data-testid="reply-ai-draft-btn">
            {replyAiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1 text-violet-500" />} Draft with AI
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className="inline-flex items-center h-7 px-2 text-xs rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors" data-testid="reply-template-btn">
                <FileText className="h-3.5 w-3.5 mr-1" /> Insert template
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64 p-1" data-testid="reply-template-menu">
              {QUICK_TEMPLATES.map((tpl, i) => (
                <button key={i} onClick={() => insertTemplate(tpl)} data-testid={`reply-template-item-${i}`}
                  className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors">
                  {tpl.label}
                </button>
              ))}
            </PopoverContent>
          </Popover>
          <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={insertBookingLink} disabled={bookingLoading} data-testid="reply-book-meeting-btn">
            {bookingLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CalendarClock className="h-3.5 w-3.5 mr-1 text-emerald-500" />} Book a meeting
          </Button>
          <Popover onOpenChange={(o) => { if (o) onOpenAttachPicker(); }}>
            <PopoverTrigger asChild>
              <button type="button" className="inline-flex items-center h-7 px-2 text-xs rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors" data-testid="reply-attach-btn">
                <Paperclip className="h-3.5 w-3.5 mr-1" /> Attach file
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-1 max-h-64 overflow-y-auto" data-testid="reply-attach-menu">
              {loadingDocs ? (
                <div className="flex items-center justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
              ) : libraryDocs.length === 0 ? (
                <p className="text-xs text-muted-foreground px-3 py-4 text-center">No documents in your library yet.</p>
              ) : libraryDocs.map((doc) => {
                const checked = replyAttachments.some((a) => a.id === doc.id);
                return (
                  <button key={doc.id} onClick={() => onToggleAttachment(doc)} data-testid={`reply-attach-doc-${doc.id}`}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors">
                    <Checkbox checked={checked} className="pointer-events-none" />
                    <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate flex-1 text-left">{doc.name}</span>
                  </button>
                );
              })}
            </PopoverContent>
          </Popover>
        </div>
        {replyAttachments.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mb-2" data-testid="reply-attachment-chips">
            {replyAttachments.map((a) => (
              <Badge key={a.id} variant="secondary" className="gap-1 pl-2 pr-1 py-0.5" data-testid={`reply-attachment-chip-${a.id}`}>
                <Paperclip className="h-3 w-3" /> <span className="truncate max-w-[140px]">{a.name}</span>
                <button onClick={() => onToggleAttachment(a)} className="ml-0.5 hover:text-destructive" data-testid={`reply-attachment-remove-${a.id}`}><X className="h-3 w-3" /></button>
              </Badge>
            ))}
          </div>
        )}
        <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder={`Reply to ${reply.lead_name}…`} rows={3} className="resize-none bg-background" data-testid="reply-composer-textarea" />
        <div className="flex items-center justify-between mt-2">
          <p className="text-[11px] text-muted-foreground truncate">Sent from your connected mailbox · threaded to {reply.lead_email || reply.lead_name}</p>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" size="sm" onClick={onCancel} data-testid="reply-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={onSend} disabled={!replyText.trim() || replySending} data-testid="reply-send-btn">
              {replySending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Send reply
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
